/**
 * CD-ICONS-01 — THE icon registry. `<Icon name size label? />`.
 *
 * One typed module for all 39 founder-ratified glyphs — the 36 base picks
 * (2026-07-26, zero overrides) plus Addendum A1's `nav.train`, `nav.runs` and
 * `sys.trash`. Names are the keys of `icons.json#icons`; the art is in
 * `icons.data.ts`, which is generated from that file by
 * `src/app/scripts/gen-icon-registry.mjs` and pinned byte-for-byte by
 * `registry.fidelity.test.ts` — which asserts the count too, so this sentence
 * cannot be the only thing keeping it honest.
 *
 * ── RENDER RULES (icons.json#direction, README §Registry) ──────────────────
 *  · 24×24 viewBox; `spot.*` are 48×48.
 *  · stroke-width 1.5 (spots: 2), round caps + joins, `fill="none"` at the
 *    root. Per-path `f: 1` → `fill="currentColor"` with NO stroke;
 *    `dash` → `stroke-dasharray`.
 *  · Glyphs SCALE LINEARLY to `size` (16 / 20 / 24 / 48). The stroke is never
 *    re-weighted per size — the viewBox does the work, which is what keeps one
 *    flame the same flame at 16 and at 48.
 *
 * ── COLOUR ─────────────────────────────────────────────────────────────────
 * `currentColor`, always — with NO exceptions. An icon never self-colours; the
 * surface's text colour sets it, and quiet chrome gets there by sitting on
 * `--ink-300`.
 *
 * ⛔ R1 (CD-ICONS-02) RETIRED THE ONE EXCEPTION. The unread dot used to be a
 * `var(--signal)` mark inside `nav.notify`; the founder ruled BADGE ONLY, so
 * unread is now the numeric badge with a `--signal` background and the glyph
 * carries no colour of its own. `--signal` still has exactly one instance in
 * the app — it just is not a glyph. There is nothing left in this module that
 * can emit a colour literal, which is why the discipline test can now assert
 * `currentColor`/`none` and nothing else, on every one of the 88.
 *
 * `--ok / --warn / --danger` belong to their status surfaces and never to a
 * minor's progress, so they are not reachable from here either.
 *
 * ── A11Y ───────────────────────────────────────────────────────────────────
 * No `label` → `aria-hidden="true"`, focusable={false} (decorative; the
 * surface's own text carries the meaning). `label` → `role="img"` +
 * `aria-label`. Touch targets stay ≥44pt — that is the CALLER's box, not the
 * glyph's, and no size here shrinks one.
 *
 * ── MOTION ─────────────────────────────────────────────────────────────────
 * `mark.celebrate` is the ONLY animated mark in the app: a stroke draw-out
 * ≤450ms, played once. `prefers-reduced-motion: reduce` renders the static
 * final frame with no animation at all (not a shortened one).
 */

import { useEffect, useRef, useState } from 'react';
import {
  CELEBRATE_DURATION_MS,
  ICONS,
  KUDO_WIRE_MAP,
  STROKE_DEFAULT,
  type IconName,
  type IconPathDef,
} from './icons.data';
import styles from './registry.module.css';

export { ICONS, ICON_NAMES, KUDO_WIRE_MAP, type IconName } from './icons.data';

/** The sizes the direction spec sanctions. Not enforced at runtime — a caller
 *  that needs 18 for one row is not a bug — but typed so the common case is
 *  the ratified one. */
export type IconSize = 16 | 20 | 24 | 48;

export interface IconProps {
  /** A key of `icons.json#icons`. */
  name: IconName;
  /** Rendered px. Glyphs scale linearly; the stroke is not re-weighted. */
  size: IconSize | number;
  /**
   * Accessible name. OMIT for decorative glyphs sitting beside their own
   * label — that is the common case and it yields `aria-hidden`. Supplying a
   * label to a glyph whose text is already read aloud makes a screen reader
   * say it twice.
   */
  label?: string;
  /**
   * `nav.active` (README §Interactions): the glyph is unchanged and a solid
   * dot is added under it, IN THE LABEL COLOUR. Composed here rather than at
   * every nav so the treatment cannot drift between SideNav and BottomTab.
   */
  active?: boolean;
  /** Extra class on the `<svg>` (positioning only — never colour). */
  className?: string;
}

/** `nav.active`'s dot lives in its own entry so both navs get the same one. */
const ACTIVE_DOT = ICONS['nav.active'].paths;

function renderPath(p: IconPathDef, i: number, stroke: number) {
  // `f: 1` is the fill vocabulary — solid means done / earned / live. Such a
  // path carries NO stroke, or the fill would be ringed and read as a
  // different mark.
  if (p.f === 1) {
    return <path key={i} d={p.d} fill="currentColor" stroke="none" />;
  }
  return (
    <path
      key={i}
      d={p.d}
      strokeWidth={stroke}
      {...(p.dash ? { strokeDasharray: p.dash } : null)}
    />
  );
}

export function Icon({
  name,
  size,
  label,
  active = false,
  className,
}: IconProps): JSX.Element {
  const def = ICONS[name];
  const stroke = def.stroke ?? STROKE_DEFAULT;
  const a11y = label
    ? ({ role: 'img', 'aria-label': label } as const)
    : ({ 'aria-hidden': true, focusable: false } as const);

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={`0 0 ${def.vb} ${def.vb}`}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...a11y}
    >
      {def.paths.map((p, i) => renderPath(p, i, stroke))}
      {/* nav.active — glyph unchanged, dot under, label colour.
          R5 binds this to the same `sys.dot` primitive the legends and the
          today-dot use; the entry below is still `nav.active`'s own path, which
          IS that primitive at r1.3 on the 24 grid. */}
      {active && ACTIVE_DOT.map((p, i) => renderPath(p, `a${i}` as unknown as number, stroke))}
    </svg>
  );
}

/**
 * `mark.celebrate` — the app's ONLY animated mark.
 *
 * Eight rays drawn out from the centre over ≤450ms, once. The draw is a
 * `stroke-dashoffset` transition rather than a keyframe animation so the
 * reduced-motion path is not "the same animation, faster" but genuinely no
 * animation: `prefers-reduced-motion: reduce` mounts at the FINAL frame and
 * never transitions.
 *
 * The media query is read in JS (not only in CSS) because the final frame has
 * to be the INITIAL state for a reduced-motion user — a CSS override that
 * lands after the first paint would still flash the undrawn frame.
 */
export function CelebrateMark({
  size = 48,
  label,
  className,
}: {
  size?: number;
  label?: string;
  className?: string;
}): JSX.Element {
  const def = ICONS['mark.celebrate'];
  const reduced = usePrefersReducedMotion();
  // Start undrawn, then flip to drawn on the next frame so the transition has
  // two states to move between. Reduced motion starts (and stays) drawn.
  const [drawn, setDrawn] = useState(reduced);
  useEffect(() => {
    if (reduced) {
      setDrawn(true);
      return;
    }
    const id = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(id);
  }, [reduced]);

  const a11y = label
    ? ({ role: 'img', 'aria-label': label } as const)
    : ({ 'aria-hidden': true, focusable: false } as const);

  return (
    <svg
      className={[styles.celebrate, className].filter(Boolean).join(' ')}
      data-drawn={drawn || undefined}
      data-reduced={reduced || undefined}
      width={size}
      height={size}
      viewBox={`0 0 ${def.vb} ${def.vb}`}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ ['--celebrate-ms' as string]: `${CELEBRATE_DURATION_MS}ms` }}
      {...a11y}
    >
      {def.paths.map((p, i) => (
        <path key={i} className={styles.ray} d={p.d} strokeWidth={def.stroke ?? STROKE_DEFAULT} />
      ))}
    </svg>
  );
}

/** `(prefers-reduced-motion: reduce)`, read once and kept live. */
function usePrefersReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const initial = useRef<boolean>(
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );
  const [reduced, setReduced] = useState(initial.current);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const onChange = () => setReduced(mq.matches);
    onChange();
    // Safari < 14 has no addEventListener on MediaQueryList.
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);
  return reduced;
}

/**
 * Kudo wire id → display art (`icons.json#kudoWireMap`).
 *
 * ⛔ The wire ids in `lib/kudoTypes.ts` are FROZEN and this lane does not touch
 * them, their order, or their payloads (README §Kudos, ratification.md §Routed
 * out 2). This is a DISPLAY-layer lookup and nothing else: an unknown id (a
 * server that learned a 7th type before the client did) resolves to `null`, and
 * the caller keeps its existing fallback rather than getting an invented glyph.
 */
export function kudoIcon(wireId: string | null | undefined): IconName | null {
  if (!wireId) return null;
  return (KUDO_WIRE_MAP as Record<string, IconName>)[wireId] ?? null;
}
